import {
  DirectionsRenderer,
  GoogleMap, Marker, StreetViewPanorama, useJsApiLoader,
} from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
// import * as turf from '@turf/turf';
import {
  addWeeks, differenceInDays, endOfDay, endOfToday,
  isAfter,
} from 'date-fns';
import {
  FinishedContainer,
  FinishedTitle,
  InfoText,
  InfoTitle,
  LabelContainer,
  LoadingContainer,
  MapContainer, MedalRequestContainer, MedalRequestInfo, ProgressContainer, ProgressInfoContainer, ProgressInfoWrapper, ProgressLabel, ProgressValue,
  RegisterButton,
  RegisterButtonText,
  StatusInfoContainer,
} from './styles';
import ProgressBar from '../../components/ProgressBar';
import RegisterActivity from '../../components/RegisterActivity';
import { refreshActivities } from '../../store/Activities.store';
import { RootState } from '../../store';
import theme from '../../theme';
import coreApi from '../../services/coreApi';
import MedalRequestModal from '../../components/MedalRequest';
import { ChallengeDetailType, ChallengeType, MedalRequestType } from '../../types/Challenger';
import Origin from '../../assets/challenges/icone-largada.png';
import Destination from '../../assets/challenges/icone-chegada.png';
import StreetIcon from '../../assets/challenges/street-view.png';
import Runner from '../../assets/challenges/icone-usuario.png';
// import challengeMapStyle from './mapStyle';
import Loader from '../../components/Loader';
import { ChallengeStatesTypes } from '.';

interface Props {
  challenge: ChallengeType,
  challengeDetail: ChallengeDetailType,
  setChallenge: React.Dispatch<React.SetStateAction<ChallengeType | undefined>>
}

export default function ChallengeProgress({ challenge, challengeDetail, setChallenge }: Props) {
  const { isLoaded: mapIsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API || '',
  });

  const { user } = useSelector((state: RootState) => state.auth);
  const { maps } = window.google;
  const dispatch = useDispatch();

  const streetViewRef = useRef<any>();

  const isDateType = challengeDetail.type === 'DATE' && challengeDetail.startDate && challengeDetail.endDate;

  const [directions, setDirections] = useState<any>();
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMedalRequest, setModalMedalRequest] = useState(false);
  const [medalRequest, setMedalRequest] = useState<MedalRequestType>();
  const [distance, setDistance] = useState(0);
  const [showStreetView, setShowStreetView] = useState(false);

  const [loading, setLoading] = useState(false);
  const [medalLoading, setMedalLoading] = useState(false);

  const [currentPoint, setCurrentPoint] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState<any>(null);
  const mapRef = useRef<any>(null);

  const startedDate = isDateType ? new Date(challengeDetail.startDate!) : new Date(challenge.started_date);
  startedDate.setHours(0, 0, 0, 0);

  const finishDate = isDateType ? new Date(challengeDetail.endDate!) : addWeeks(startedDate, challenge.weeks);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const totalDays = differenceInDays(finishDate, startedDate);
  const remainingDays = differenceInDays(finishDate, today);
  const passedDays = differenceInDays(today, startedDate);

  const daysProgress = (passedDays / totalDays) * 100;

  const totalDistance = challengeDetail.distance;
  const remainingDistance = totalDistance - distance;
  const distanceProgress = (distance / totalDistance) * 100;

  const waypoints = mapIsLoaded ? challengeDetail.waypoints.map((point) => ({
    location: new maps.LatLng(point.lat, point.lng),
    stopover: false,
  })) : [];

  const tranformTravelMode = (mode: string) => {
    switch (mode) {
      case 'BICYCLING':
        return maps.TravelMode.BICYCLING;
      case 'DRIVING':
        return maps.TravelMode.DRIVING;
      case 'WALKING':
        return maps.TravelMode.WALKING;
      default:
        return maps.TravelMode.BICYCLING;
    }
  };

  const options = mapIsLoaded ? {
    // style: challengeMapStyle,
    disableDefaultUI: false,
    fullscreenControl: false,
    clickableIcons: false,
    streetViewControl: false,
    mapTypeControl: true,
    mapTypeId: 'roadmap',
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.DEFAULT,
      position: maps.ControlPosition.TOP_RIGHT,
      mapTypeIds: [
        maps.MapTypeId.ROADMAP,
        maps.MapTypeId.SATELLITE,
        maps.MapTypeId.HYBRID,
      ],
    },
  } : {};

  const createStreetViewButton = () => {
    const controlButton = document.createElement('button');
    const image = document.createElement('img');

    image.src = StreetIcon;
    image.alt = 'Street View Button';
    image.width = 30;
    image.height = 30;

    controlButton.style.border = '2px solid #fff';
    controlButton.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
    controlButton.style.cursor = 'pointer';
    controlButton.style.textAlign = 'center';
    controlButton.style.marginRight = '10px';
    controlButton.style.padding = '3px';
    controlButton.type = 'button';

    controlButton.appendChild(image);

    controlButton.addEventListener('click', () => {
      setShowStreetView(true);
    });

    return controlButton;
  };

  const { origin, destination } = challengeDetail;

  const updateChallenge = async (status: ChallengeStatesTypes) => {
    coreApi.put(`challenge/${challenge?._id}`, { status })
      .then((response) => {
        if (response.data) {
          setChallenge(response.data);
          toast.success('Desafio finalizado!');
        }
      })
      .catch(() => {
        toast.error('Erro ao atualizar o desafio');
      });
  };

  const verifyDate = () => {
    if (challenge.status === ChallengeStatesTypes.FINISHED || challenge.status === ChallengeStatesTypes.OUT_OF_DATE) return;
    if (challengeDetail.type === 'DATE' && challengeDetail.endDate) {
      const endDate = endOfDay(new Date(challengeDetail.endDate));
      const today = endOfToday();

      if (isAfter(today, endDate)) {
        updateChallenge(ChallengeStatesTypes.OUT_OF_DATE);
      }
    }
  };

  const getDistance = async () => {
    setLoading(true);
    coreApi.get(`/user-activity/challenge/${user.id}/${challengeDetail.slug}?start_date=${startedDate}&end_date=${challengeDetail.endDate ?? ''}`)
      .then((response) => {
        if (response.data) {
          setDistance(response.data.distance);
          if (response.data.distance >= totalDistance && challenge.status !== ChallengeStatesTypes.FINISHED) {
            updateChallenge(ChallengeStatesTypes.FINISHED);
          }
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error('Erro ao calcular a distância');
        setLoading(false);
      });
  };

  const refresh = () => {
    toast.success('Atividade criada com sucesso!');
    getDistance();
    dispatch(refreshActivities(user.id) as any);
  };

  function updateUserPosition(distance: number) {
    if (!directions) return null;
    let distanceTraveled = distance * 1000;
    const distancePerLap = (challengeDetail.distance / challengeDetail.laps) * 1000;
    if (challengeDetail.hasLaps && challengeDetail.laps && distanceTraveled > distancePerLap) {
      distanceTraveled %= distancePerLap;
    }
    let coveredDistance = 0;
    const routePath = directions.routes[0].overview_path;
    for (let i = 0; i < routePath.length - 1; i++) {
      const segmentDistance = maps.geometry.spherical.computeDistanceBetween(
        routePath[i],
        routePath[i + 1],
      );

      if (coveredDistance + segmentDistance >= distanceTraveled) {
        const ratio = (distanceTraveled - coveredDistance) / segmentDistance;
        const lat = routePath[i].lat() + ratio * (routePath[i + 1].lat() - routePath[i].lat());
        const lng = routePath[i].lng() + ratio * (routePath[i + 1].lng() - routePath[i].lng());

        return new maps.LatLng(lat, lng);
      }
      coveredDistance += segmentDistance;
    }
    return null;
  }

  const getMedalRequest = () => {
    setMedalLoading(true);
    if (challenge.status === ChallengeStatesTypes.FINISHED && challengeDetail.hasMedalRequest) {
      coreApi.get(`medal-request/user/${user.id}/${challenge.slug}`)
        .then((response) => {
          if (response.data) {
            setMedalRequest(response.data);
          }
          setMedalLoading(false);
        }).catch(() => {
          setMedalLoading(false);
        });
    }
  };

  const getCurrenteLap = () => {
    const lap = Math.floor(distance / (challengeDetail.distance / challengeDetail.laps)) + 1;
    return Math.min(lap, challengeDetail.laps);
  };

  useEffect(() => {
    if (mapIsLoaded) {
      const DirectionsService = new maps.DirectionsService();

      DirectionsService.route({
        origin: new maps.LatLng(origin.lat, origin.lng),
        destination: new maps.LatLng(destination.lat, destination.lng),
        travelMode: tranformTravelMode(challengeDetail.routeType),
        waypoints,
      }, (result, status) => {
        if (status === maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          toast.error('Erro ao carregar direções do mapa');
        }
      });
    }
  }, [mapIsLoaded]);

  useEffect(() => {
    getDistance();
    verifyDate();
  }, [challenge]);

  useEffect(() => {
    if (challengeDetail.hasMedalRequest) {
      getMedalRequest();
    }
  }, [challenge]);

  useEffect(() => {
    if (distance >= totalDistance) {
      setCurrentPoint(destination);
      setMapCenter(destination);
    } else if (distance > 0) {
      const userPosition = updateUserPosition(distance);
      setCurrentPoint(userPosition);
      setMapCenter(userPosition);
    } else {
      setCurrentPoint(null);
      setMapCenter(origin);
    }
  }, [distance, directions]);

  useEffect(() => {
    if (directions && mapRef.current && mapCenter) {
      if (mapRef.current && mapCenter) {
        mapRef.current.panTo(mapCenter);
        mapRef.current.setZoom(15);
      }
    }
  }, [directions, mapCenter]);

  return (
    <>
      <RegisterActivity
        visible={modalVisible}
        close={() => setModalVisible(false)}
        refresh={refresh}
        _type="Run"
      />
      <MedalRequestModal isVisible={modalMedalRequest} setVisible={setModalMedalRequest} user_id={user.id} challenge={challenge} refresh={getMedalRequest} />
      {!loading && (
        <>
          {mapIsLoaded && (
            <MapContainer>
              <GoogleMap
                mapContainerStyle={{
                  width: '100%',
                  height: '100%',
                }}
                center={mapCenter || origin}
                zoom={15}
                options={options}
                onLoad={(map) => {
                  mapRef.current = map;
                  const controlDiv = document.createElement('div');

                  const streetViewButton = createStreetViewButton();

                  controlDiv.appendChild(streetViewButton);

                  map.controls[maps.ControlPosition.RIGHT_BOTTOM].push(controlDiv);
                }}
              >
                {directions
                  && (
                    <DirectionsRenderer
                      directions={directions}
                      options={{
                        suppressMarkers: true,
                        suppressBicyclingLayer: true,
                        preserveViewport: true,
                      }}
                    />
                  )}
                <Marker
                  position={origin}
                  icon={{
                    url: Origin,
                    scaledSize: new maps.Size(40, 40),
                    origin: new maps.Point(0, 0),
                  }}
                />
                <Marker
                  position={destination}
                  icon={{
                    url: Destination,
                    scaledSize: new maps.Size(40, 40),
                    origin: new maps.Point(0, 0),
                  }}
                />
                {currentPoint && distance > 0 && (
                  <Marker
                    position={currentPoint}
                    icon={{
                      url: Runner,
                      scaledSize: new maps.Size(30, 40),
                      origin: new maps.Point(0, 0),
                    }}
                  />
                )}
                {currentPoint && (
                  <StreetViewPanorama
                    ref={streetViewRef}
                    options={{
                      position: currentPoint,
                      visible: showStreetView,
                      fullscreenControl: false,
                      enableCloseButton: true,
                      addressControlOptions: { position: maps.ControlPosition.TOP_RIGHT },
                    }}
                    onVisibleChanged={() => {
                      if (streetViewRef.current && streetViewRef.current.state.streetViewPanorama.visible === false) {
                        setShowStreetView(false);
                      }
                    }}
                  />
                )}
              </GoogleMap>
            </MapContainer>
          )}
          {challenge.status === ChallengeStatesTypes.IN_PROGRESS && (
            <ProgressContainer>
              <ProgressInfoWrapper>
                <ProgressInfoContainer>
                  <LabelContainer>
                    <ProgressLabel color="#00CD00">Concluídos</ProgressLabel>
                    <ProgressValue>{`${distance.toFixed(1)}km`}</ProgressValue>
                  </LabelContainer>
                  {challengeDetail.hasLaps && (
                    <LabelContainer>
                      <ProgressLabel color={theme.colors.notifications}>Voltas</ProgressLabel>
                      <ProgressValue>{`${getCurrenteLap()}/${challengeDetail.laps}`}</ProgressValue>
                    </LabelContainer>
                  )}
                  <LabelContainer>
                    <ProgressLabel>Restantes</ProgressLabel>
                    <ProgressValue>{`${remainingDistance.toFixed(1)}km`}</ProgressValue>
                  </LabelContainer>
                </ProgressInfoContainer>
              </ProgressInfoWrapper>
              <ProgressBar label="distância" color="#00CD00" value={distanceProgress} />
              <ProgressBar label="Tempo" color="#D2E6B0" value={daysProgress} />
              <ProgressInfoWrapper>
                <ProgressInfoContainer>
                  <LabelContainer>
                    <ProgressLabel color="#D2E6B0">Decorridos</ProgressLabel>
                    <ProgressValue>{`${passedDays} Dias`}</ProgressValue>
                  </LabelContainer>
                  <LabelContainer>
                    <ProgressLabel>Restantes </ProgressLabel>
                    <ProgressValue>{`${remainingDays} Dias`}</ProgressValue>
                  </LabelContainer>
                </ProgressInfoContainer>
              </ProgressInfoWrapper>
              <RegisterButton onClick={() => setModalVisible(true)}>
                <RegisterButtonText>Registrar atividade manual</RegisterButtonText>
              </RegisterButton>
            </ProgressContainer>
          )}
          {challenge.status === ChallengeStatesTypes.FINISHED && (
            <FinishedContainer>
              <FinishedTitle>Parabéns, você concluiu o desafio!</FinishedTitle>
              <ProgressInfoContainer full>
                <LabelContainer>
                  <ProgressLabel color="#00CD00">Concluídos</ProgressLabel>
                  <ProgressValue>{`${totalDistance}km`}</ProgressValue>
                </LabelContainer>
                <LabelContainer>
                  <ProgressLabel>Restantes</ProgressLabel>
                  <ProgressValue>0 km</ProgressValue>
                </LabelContainer>
              </ProgressInfoContainer>
              <ProgressBar label="" color="#00CD00" value={distanceProgress} full />
              {challengeDetail.hasMedalRequest && (
                <>
                  {!medalRequest && !medalLoading && (
                    <RegisterButton onClick={() => setModalMedalRequest(true)} color="white">
                      <RegisterButtonText color={theme.colors.primary}>Solicite sua medalha</RegisterButtonText>
                    </RegisterButton>
                  )}
                  {medalRequest && medalRequest?.status === 'SENT' && !medalLoading && (
                    <MedalRequestContainer>
                      <MedalRequestInfo>Medalha enviada.</MedalRequestInfo>
                      <MedalRequestInfo>Confira em seu e-mail o link de rastreio da remessa.</MedalRequestInfo>
                    </MedalRequestContainer>
                  )}
                  {medalRequest && medalRequest?.status === 'REQUESTED' && !medalLoading && (
                    <MedalRequestContainer>
                      <MedalRequestInfo>Medalha em preparação para envio.</MedalRequestInfo>
                      <MedalRequestInfo>Você receberá o link de rastreio em breve no seu e-mail.</MedalRequestInfo>
                    </MedalRequestContainer>
                  )}
                </>
              )}
              {challengeDetail.customFinishedMessage && (
                <StatusInfoContainer>
                  <InfoText>{challengeDetail.customFinishedMessage}</InfoText>
                </StatusInfoContainer>
              )}
            </FinishedContainer>
          )}
          {challenge.status === ChallengeStatesTypes.OUT_OF_DATE && (
            <ProgressContainer>
              <ProgressInfoWrapper>
                <ProgressInfoContainer>
                  <LabelContainer>
                    <ProgressLabel color="#00CD00">Concluídos</ProgressLabel>
                    <ProgressValue>{`${distance.toFixed(1)}km`}</ProgressValue>
                  </LabelContainer>
                  <LabelContainer>
                    <ProgressLabel>Restantes</ProgressLabel>
                    <ProgressValue>{`${remainingDistance.toFixed(1)}km`}</ProgressValue>
                  </LabelContainer>
                </ProgressInfoContainer>
              </ProgressInfoWrapper>
              <ProgressBar label="distância" color="#00CD00" value={distanceProgress} />
              <ProgressBar label="Tempo" color="#D2E6B0" value={daysProgress} />
              <ProgressInfoWrapper>
                <ProgressInfoContainer>
                  <LabelContainer>
                    <ProgressLabel color="#D2E6B0">Decorridos</ProgressLabel>
                    <ProgressValue>{`${differenceInDays(finishDate, startedDate)} Dias`}</ProgressValue>
                  </LabelContainer>
                  <LabelContainer>
                    <ProgressLabel>Restantes </ProgressLabel>
                    <ProgressValue>{`${Math.max(remainingDays, 0)} Dias`}</ProgressValue>
                  </LabelContainer>
                </ProgressInfoContainer>
              </ProgressInfoWrapper>
              <StatusInfoContainer>
                <InfoTitle>O prazo deste desafio chegou ao fim.</InfoTitle>
                <InfoText>Informamos que o período de participação neste desafio foi encerrado.</InfoText>
              </StatusInfoContainer>
            </ProgressContainer>
          )}
        </>
      )}
      {loading && (
        <LoadingContainer>
          <Loader color="white" />
        </LoadingContainer>
      )}
    </>
  );
}
